import { Feature } from '@/enums/feature';
import { FeatureRole, OrganizationRole } from '@/enums/roles';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import type { OrganizationEmployee } from '@/types/employee';
import type { NameIdPair } from '@/types/nameIdPair';
import { type AccessibleOrganization, type Organization } from '@/types/organization';
import { roleFeatureMap } from '@/utils/role';

export function getEmployeeFromOrganization(organization: Organization, employeeId: string): OrganizationEmployee | null {
    const employee = organization.employees?.find((employeeProps) => employeeProps.employee == employeeId);

    return employee ? employee : null;
}

function mapAccessibleOrgsToNameIdPairs(orgs: AccessibleOrganization[]): NameIdPair[] {
    return orgs.flatMap((org) => [mapOrgToNameIdPair(org), ...(org.children ? mapAccessibleOrgsToNameIdPairs(org.children) : [])]);
}

function mapOrgToNameIdPair(org: Organization): NameIdPair {
    return { _id: org._id, name: org.name };
}

export function getAllAccessibleOrgsFlat() {
    const authStore = useAuthStore();

    if (authStore.isSuperAdmin())
        return (
            authStore.accessibleOrganizations?.flatMap((org) => [
                mapOrgToNameIdPair(org),
                ...(org.children ? mapAccessibleOrgsToNameIdPairs(org.children) : [])
            ]) ?? []
        );

    return authStore.accessibleOrganizations ?? [];
}

/** Returns all the available roles for the current organization. If no organization is provided the current organization is used */
export async function getAvailableRoles(organizationId?: string) {
    const features = await getOrgFeatures(organizationId);
    const roles = [...Object.values(OrganizationRole), ...Object.values(FeatureRole)];

    return roles.filter((role) => {
        const requiredFeatureForRole = roleFeatureMap[role as FeatureRole];
        return !requiredFeatureForRole || features.includes(requiredFeatureForRole);
    });
}

/** Checks if an organization has a feature, if no organization is provided the current organization is used */
export async function orgHasFeature(feature: Feature, organizationId?: string) {
    const features = await getOrgFeatures(organizationId);
    return features.includes(feature);
}

async function getOrgFeatures(organizationId?: string) {
    if (!organizationId) {
        const authStore = useAuthStore();
        organizationId = authStore.getCurrentOrganization()._id;
    }

    const organizationStore = useOrganizationStore();
    return await organizationStore.getFeatures(organizationId);
}
